import React, { Dispatch, SetStateAction } from 'react'
import { jsx, useThemeUI, merge, Context } from '@theme-ui/core'
import { get, Theme } from '@theme-ui/css'
import { Global, ThemeContext as EmotionContext } from '@emotion/react'
import { toCustomProperties, createColorStyles } from './custom-properties'

const STORAGE_KEY = 'theme-ui-color-mode'

declare module '@theme-ui/core' {
  export interface ContextValue {
    colorMode?: string
    setColorMode?: (colorMode: SetStateAction<string>) => void
  }
}

const storage = {
  get: (init?: string) => {
    try {
      return window.localStorage.getItem(STORAGE_KEY) || init
    } catch (e) {
      console.warn(
        'localStorage is disabled and color mode might not work as expected.',
        'Please check your Site Settings.',
        e
      )
    }
  },
  set: (value: string) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, value)
    } catch (e) {
      console.warn(
        'localStorage is disabled and color mode might not work as expected.',
        'Please check your Site Settings.',
        e
      )
    }
  },
}

const getPreferredColorScheme = (): 'dark' | 'light' | null => {
  if (typeof window !== 'undefined' && window.matchMedia) {
    if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark'
    }
    if (window.matchMedia('(prefers-color-scheme: light)').matches) {
      return 'light'
    }
  }
  return null
}

const useColorModeState = (theme: Theme = {}) => {
  const [mode, setMode] = React.useState(() => {
    const preferredMode =
      theme.useColorSchemeMediaQuery !== false && getPreferredColorScheme()

    return preferredMode || theme.initialColorModeName || 'default'
  })

  // read color mode from local storage
  React.useEffect(() => {
    const stored = theme.useLocalStorage !== false && storage.get()
    document.documentElement.classList.remove('theme-ui-' + stored)
    document.body.classList.remove('theme-ui-' + stored)

    if (stored && stored !== mode) {
      setMode(stored)
    }
  }, [])

  React.useEffect(() => {
    if (mode && theme.useLocalStorage !== false) {
      storage.set(mode)
    }
  }, [mode])

  if (process.env.NODE_ENV !== 'production') {
    if (
      theme.colors &&
      theme.colors.modes &&
      theme.initialColorModeName &&
      Object.keys(theme.colors.modes).indexOf(theme.initialColorModeName) > -1
    ) {
      console.warn(
        'The `initialColorModeName` value should be a unique name' +
          ' and cannot reference a key in `theme.colors.modes`.'
      )
    }
  }

  return [mode, setMode] as const
}

export function useColorMode<T extends string = string>(): [
  T,
  Dispatch<SetStateAction<T>>
] {
  const { colorMode, setColorMode } = useThemeUI()

  if (typeof setColorMode !== 'function') {
    throw new Error(`[useColorMode] requires the ColorModeProvider component`)
  }

  // We're allowing the user to specify a narrower type for its color mode name.
  return ([colorMode, setColorMode] as unknown) as [
    T,
    Dispatch<SetStateAction<T>>
  ]
}

const applyColorMode = (theme: Theme, mode: string): Theme => {
  if (!mode) return theme
  const modes = get(theme, 'colors.modes', {})
  return merge.all({}, theme, {
    colors: get(modes, mode, {}),
  })
}

const BodyStyles = ({ theme }: { theme: Theme }) =>
  jsx(Global, {
    styles: () => {
      return createColorStyles(theme)
    },
  })

export const ColorModeProvider: React.FC = ({ children }) => {
  const outer = useThemeUI()
  const [colorMode, setColorMode] = useColorModeState(outer.theme)
  const theme = applyColorMode(outer.theme || {}, colorMode)
  const emotionTheme = { ...theme }

  if (theme.useCustomProperties !== false) {
    emotionTheme.colors = toCustomProperties(emotionTheme.colors, 'colors')
  }

  const context = {
    ...outer,
    theme,
    colorMode,
    setColorMode,
  }

  return jsx(
    EmotionContext.Provider,
    { value: emotionTheme },
    jsx(
      Context.Provider,
      { value: context },
      jsx(BodyStyles, { key: 'color-mode', theme }),
      children
    )
  )
}

const noflash = `(function() { try {
  var mode = localStorage.getItem('theme-ui-color-mode');
  if (!mode) return
  document.documentElement.classList.add('theme-ui-' + mode);
  document.body.classList.add('theme-ui-' + mode);
} catch (e) {} })();`

export const InitializeColorMode = () =>
  jsx('script', {
    key: 'theme-ui-no-flash',
    dangerouslySetInnerHTML: {
      __html: noflash,
    },
  })
